import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  limit,
  addDoc,
  writeBatch,
  updateDoc,
} from "firebase/firestore";
import firebaseApp from "@/lib/firebase";
import type {
  CourseModulesList,
  ModuleBlocksList,
  SchoolCoursesList,
  MemberCoursesList,
} from "../types/edgeTypes";

/**
 * Repository class for edge relationships between entities
 * Handles the linking and ordering of courses, modules, and content blocks
 */
export class ListRepository {
  private db = getFirestore(firebaseApp);
  private school_courses_collection = "schoolCoursesList" as const;
  private course_modules_collection = "courseModulesList" as const;
  private module_blocks_collection = "moduleBlocksList" as const;
  private member_courses_collection = "memberCoursesList" as const;

  /**
   * Get all courses ids for a school
   */
  async getCoursesIdsForSchool(schoolId: string): Promise<string[]> {
    const q = query(
      collection(this.db, this.school_courses_collection),
      where("schoolId", "==", schoolId),
      limit(1)
    );
    const snap = await getDocs(q);
    return snap.docs.find((d) => d.id)?.data().coursesIds || [];
  }

  /**
   * Get all modules ids for a course
   */
  async getModuleIdsForCourse(courseId: string): Promise<string[]> {
    const q = query(
      collection(this.db, this.course_modules_collection),
      where("courseId", "==", courseId),
      limit(1)
    );
    const snap = await getDocs(q);
    return snap.docs.find((d) => d.id)?.data().modulesIds || [];
  }

  /**
   * Get all blocks ids for a module
   */
  async getBlocksIdsForModule(moduleId: string): Promise<string[]> {
    const q = query(
      collection(this.db, this.module_blocks_collection),
      where("moduleId", "==", moduleId),
      limit(1)
    );
    const snap = await getDocs(q);
    return snap.docs.find((d) => d.id)?.data().blocksIds || [];
  }

  /**
   * Get all courses ids for a member
   */
  async getCoursesIdsForSchoolMember(memberId: string): Promise<string[]> {
    const q = query(
      collection(this.db, this.member_courses_collection),
      where("memberId", "==", memberId),
      limit(1)
    );
    const snap = await getDocs(q);
    return snap.docs.find((d) => d.id)?.data().coursesIds || [];
  }

  /**
   * Get school courses by school Id
   */
  async getSchoolCourses(schoolId: string): Promise<SchoolCoursesList | null> {
    const q = query(
      collection(this.db, this.school_courses_collection),
      where("schoolId", "==", schoolId),
      limit(1)
    );
    // Execute the query and return the data
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const doc = snap.docs[0];
    return { id: doc.id, ...doc.data() } as SchoolCoursesList;
  }

  /**
   * Get course modules by course ID
   */
  async getCourseModules(courseId: string): Promise<CourseModulesList | null> {
    const q = query(
      collection(this.db, this.course_modules_collection),
      where("courseId", "==", courseId),
      limit(1)
    );
    // Execute the query and return the data
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const doc = snap.docs[0];
    return { id: doc.id, ...doc.data() } as CourseModulesList;
  }

  /**
   * Get module blocks by module ID
   */
  async getModuleBlocks(moduleId: string): Promise<ModuleBlocksList | null> {
    const q = query(
      collection(this.db, this.module_blocks_collection),
      where("moduleId", "==", moduleId),
      limit(1)
    );
    // Execute the query and return the data
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const doc = snap.docs[0];
    return { id: doc.id, ...doc.data() } as ModuleBlocksList;
  }

  /**
   * Get the first block for a course
   */
  async getFirstBlockIdForCourse(courseId: string): Promise<string | null> {
    const firstModuleId = await this.getFirstModuleIdForCourse(courseId);
    if (!firstModuleId) return null;
    const firstBlockId = await this.getFirstBlockIdForModule(firstModuleId);
    if (!firstBlockId) return null;
    return firstBlockId;
  }

  /**
   * Get the first module for a course
   */
  async getFirstModuleIdForCourse(courseId: string): Promise<string | null> {
    const q = query(
      collection(this.db, this.course_modules_collection),
      where("courseId", "==", courseId),
      limit(1)
    );
    const snap = await getDocs(q);
    const doc = snap.docs[0];
    return doc ? doc.data().modulesIds[0] || null : null;
  }

  /**
   * Get the first block for a module
   */
  async getFirstBlockIdForModule(moduleId: string): Promise<string | null> {
    const q = query(
      collection(this.db, this.module_blocks_collection),
      where("moduleId", "==", moduleId),
      limit(1)
    );
    const snap = await getDocs(q);
    const doc = snap.docs[0];
    return doc ? doc.data().blocksIds[0] || null : null;
  }

  async createSchoolCoursesList(
    schoolCoursesEdge: Omit<SchoolCoursesList, "id">
  ): Promise<string> {
    const docRef = await addDoc(
      collection(this.db, this.school_courses_collection),
      schoolCoursesEdge
    );
    return docRef.id;
  }

  async createCourseModulesList(
    courseModulesEdge: Omit<CourseModulesList, "id">
  ): Promise<string> {
    const docRef = await addDoc(
      collection(this.db, this.course_modules_collection),
      courseModulesEdge
    );
    return docRef.id;
  }

  async createModuleBlocksList(
    moduleBlocksList: Omit<ModuleBlocksList, "id">
  ): Promise<string> {
    const docRef = await addDoc(
      collection(this.db, this.module_blocks_collection),
      moduleBlocksList
    );
    return docRef.id;
  }

  async createMemberCoursesList(
    memberCoursesEdge: Omit<MemberCoursesList, "id">
  ): Promise<string> {
    const docRef = await addDoc(
      collection(this.db, this.member_courses_collection),
      memberCoursesEdge
    );
    return docRef.id;
  }

  async upsertSchoolCoursesList(
    schoolId: string,
    coursesIds: string[]
  ): Promise<void> {
    const schoolCoursesQuery = query(
      collection(this.db, this.school_courses_collection),
      where("schoolId", "==", schoolId)
    );

    const schoolCoursesSnap = await getDocs(schoolCoursesQuery);

    if (schoolCoursesSnap.empty) {
      await this.createSchoolCoursesList({ schoolId, coursesIds });
      return;
    } else {
      const currentCoursesIds: string[] = [];
      schoolCoursesSnap.forEach((doc) => {
        currentCoursesIds.push(...doc.data().coursesIds);
      });

      const mergedCoursesIds = Array.from(
        new Set([...currentCoursesIds, ...coursesIds])
      );

      const batch = writeBatch(this.db);
      schoolCoursesSnap.forEach((doc) => {
        const docRef = doc.ref;
        batch.update(docRef, { coursesIds: mergedCoursesIds });
      });
      await batch.commit();
    }
  }

  async upsertCourseModulesList(
    courseModules: Omit<CourseModulesList, "id">
  ): Promise<void> {
    const existingEdgesQuery = query(
      collection(this.db, this.course_modules_collection),
      where("courseId", "==", courseModules.courseId),
      limit(1)
    );

    const existingEdgesSnap = await getDocs(existingEdgesQuery);
    //write blocksIds to existing or create new
    if (existingEdgesSnap.empty) {
      await this.createCourseModulesList(courseModules);
      return;
    } else {
      const doc = existingEdgesSnap.docs[0];
      const docRef = doc.ref;
      await updateDoc(docRef, { modulesIds: courseModules.modulesIds });
    }
  }

  async upsertModuleBlocksList(
    moduleBlocks: Omit<ModuleBlocksList, "id">
  ): Promise<void> {
    const existingEdgesQuery = query(
      collection(this.db, this.module_blocks_collection),
      where("moduleId", "==", moduleBlocks.moduleId),
      limit(1)
    );

    const existingEdgesSnap = await getDocs(existingEdgesQuery);
    //write blocksIds to existing or create new
    if (existingEdgesSnap.empty) {
      await this.createModuleBlocksList(moduleBlocks);
      return;
    } else {
      const doc = existingEdgesSnap.docs[0];
      const docRef = doc.ref;
      await updateDoc(docRef, { blocksIds: moduleBlocks.blocksIds });
    }
  }

  async upsertMemberCoursesList(
    memberCourses: Omit<MemberCoursesList, "id">
  ): Promise<void> {
    const existingEdgesQuery = query(
      collection(this.db, this.member_courses_collection),
      where("memberId", "==", memberCourses.memberId),
      limit(1)
    );

    const existingEdgesSnap = await getDocs(existingEdgesQuery);
    //write coursesIds to existing or create new
    if (existingEdgesSnap.empty) {
      await this.createMemberCoursesList(memberCourses);
      return;
    } else {
      const doc = existingEdgesSnap.docs[0];
      const docRef = doc.ref;
      await updateDoc(docRef, { coursesIds: memberCourses.coursesIds });
    }
  }
}

// Export singleton instance
export const listRepository = new ListRepository();
